/**
 * Persist live session timing (elapsed, pause, topic start gate, actual times).
 */

import type { TopicTimingRecord } from "./types";

export type LiveTimingPersisted = {
  sessionElapsedSec: number;
  /** Wall-clock epoch ms when the session duration timer started. */
  sessionAnchorMs?: number;
  topicElapsedSec: number;
  topicTimeBonusSec: number;
  pauseAccumulatedSec: number;
  topicStarted: boolean;
  currentTopicId: string | null;
  topicsTiming: Record<
    string,
    {
      actualStartAt?: string;
      actualEndAt?: string;
      actualDurationSec?: number;
      skipped?: boolean;
      subtopics?: Record<
        string,
        {
          actualStartAt?: string;
          actualEndAt?: string;
          actualDurationSec?: number;
          skipped?: boolean;
        }
      >;
    }
  >;
  updatedAt: number;
};

const KEY = (sessionId: string) => `codelive-live-timing:${sessionId}`;

export function loadLiveTiming(sessionId: string): LiveTimingPersisted | null {
  try {
    const raw = sessionStorage.getItem(KEY(sessionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LiveTimingPersisted>;
    return {
      sessionElapsedSec: Math.max(0, Number(parsed.sessionElapsedSec) || 0),
      sessionAnchorMs:
        Number(parsed.sessionAnchorMs) > 0
          ? Number(parsed.sessionAnchorMs)
          : undefined,
      topicElapsedSec: Math.max(0, Number(parsed.topicElapsedSec) || 0),
      topicTimeBonusSec: Math.max(0, Number(parsed.topicTimeBonusSec) || 0),
      pauseAccumulatedSec: Math.max(0, Number(parsed.pauseAccumulatedSec) || 0),
      topicStarted: Boolean(parsed.topicStarted),
      currentTopicId:
        parsed.currentTopicId == null ? null : String(parsed.currentTopicId),
      topicsTiming:
        parsed.topicsTiming && typeof parsed.topicsTiming === "object"
          ? parsed.topicsTiming
          : {},
      updatedAt: Number(parsed.updatedAt) || Date.now(),
    };
  } catch {
    return null;
  }
}

export function saveLiveTiming(
  sessionId: string,
  state: LiveTimingPersisted,
): void {
  try {
    sessionStorage.setItem(
      KEY(sessionId),
      JSON.stringify({ ...state, updatedAt: Date.now() }),
    );
  } catch {
    /* ignore quota */
  }
}

export function timingRecordsFromPersisted(
  topicsTiming: LiveTimingPersisted["topicsTiming"],
): TopicTimingRecord[] {
  return Object.entries(topicsTiming).map(([topic_id, row]) => ({
    topic_id,
    actual_start_at: row.actualStartAt,
    actual_end_at: row.actualEndAt,
    actual_duration_sec: row.actualDurationSec,
    skipped: row.skipped,
    subtopics: row.subtopics
      ? Object.entries(row.subtopics).map(([subtopic_id, sub]) => ({
          subtopic_id,
          actual_start_at: sub.actualStartAt,
          actual_end_at: sub.actualEndAt,
          actual_duration_sec: sub.actualDurationSec,
          skipped: sub.skipped,
        }))
      : undefined,
  }));
}

/** Drop client-only start times when the server plan has not started that topic. */
export function sanitizePersistedTopicsTiming(
  timing: LiveTimingPersisted["topicsTiming"],
  planTopics: Array<{
    id: string;
    actualStartAt?: string;
    subtopics?: Array<{ id: string; actualStartAt?: string }>;
  }>,
): LiveTimingPersisted["topicsTiming"] {
  const next = { ...timing };
  const planById = new Map(planTopics.map((t) => [t.id, t]));
  for (const [id, row] of Object.entries(next)) {
    const plan = planById.get(id);
    if (!plan) continue;
    let nextRow = row;
    if (!plan.actualStartAt && row.actualStartAt) {
      const { actualStartAt: _drop, ...rest } = row;
      nextRow = rest;
    }
    // Same for subtopics — stale local starts cause bogus 20h Time taken.
    if (nextRow.subtopics && plan.subtopics?.length) {
      const planSubs = new Map(plan.subtopics.map((s) => [s.id, s]));
      const cleanedSubs: NonNullable<typeof nextRow.subtopics> = {};
      for (const [subId, subRow] of Object.entries(nextRow.subtopics)) {
        const planSub = planSubs.get(subId);
        if (planSub && !planSub.actualStartAt && subRow.actualStartAt) {
          const { actualStartAt: _dropSub, ...subRest } = subRow;
          cleanedSubs[subId] = subRest;
        } else {
          cleanedSubs[subId] = subRow;
        }
      }
      nextRow = { ...nextRow, subtopics: cleanedSubs };
    }
    next[id] = nextRow;
  }
  return next;
}
